import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  catchError,
  combineLatest,
  forkJoin,
  map,
  Observable,
  of,
  retry,
  share,
  shareReplay,
  throwError,
  timeout,
  TimeoutError,
  timer,
} from 'rxjs';

import { ConfigService, mapErrorForMonitoring, MonitoringLogger } from '@fcom/core';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { groupBy, isPresent } from '@fcom/core/utils/utils';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { getTimetableForRoute } from '@fcom/dapi/api/fn/timetable/get-timetable-for-route';

import { TimetableSearchParams, TimetableSearchResult } from '../../interfaces/timetable-search.interface';
import {
  getDateIdsInRange,
  getEarliestSearchDayInMonth,
  getRemainingDaysInMonth,
} from '../../utils/timetable-search.utils';

type CachedDay = { flights: FinnairBoundItem[] } | { error: unknown };

@Injectable({ providedIn: 'root' })
export class TimetableSearchService {
  private configService = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);
  private dapiService = inject(DapiService);

  static readonly GET_TIMETABLE_TIMEOUT_MS = 20000;
  static readonly MAX_TIMETABLE_DAYS_PER_REQUEST = 35;
  static readonly MAX_RETRIES = 2;
  static readonly RETRY_BASE_DELAY_MS = 1000;

  // Keyed per day (not per request) so overlapping ranges from different consumers share data. Page-lifetime, no expiry.
  private readonly flightsCache = new Map<string, Map<string, Observable<CachedDay>>>();

  getTimetable(
    search: Pick<TimetableSearchParams, 'origin' | 'destination' | 'selectedMonth' | 'directOnly'>
  ): Observable<TimetableSearchResult> {
    return this.getTimetableForDays(
      search,
      getEarliestSearchDayInMonth(search.selectedMonth),
      getRemainingDaysInMonth(search.selectedMonth)
    );
  }

  getTimetableForRange(
    search: Pick<TimetableSearchParams, 'origin' | 'destination' | 'directOnly'>,
    start: LocalDate,
    end: LocalDate
  ): Observable<TimetableSearchResult> {
    const dateIds = getDateIdsInRange(start, end);
    if (dateIds.length === 0) {
      return of<TimetableSearchResult>({ availability: [], flights: {}, weeklyFlights: [] });
    }

    const maxDays = TimetableSearchService.MAX_TIMETABLE_DAYS_PER_REQUEST;
    const requests = Array.from({ length: Math.ceil(dateIds.length / maxDays) }, (_, index) => {
      const offset = index * maxDays;
      return this.getTimetableForDays(
        search,
        new LocalDate(dateIds[offset]),
        Math.min(maxDays, dateIds.length - offset)
      );
    });

    return forkJoin(requests).pipe(
      map((results) => {
        return results.reduce<TimetableSearchResult>(
          (merged, result) => {
            return {
              availability: [...merged.availability, ...(result.availability ?? [])],
              flights: { ...merged.flights, ...result.flights },
              weeklyFlights: [...merged.weeklyFlights, ...(result.weeklyFlights ?? [])],
            };
          },
          { availability: [], flights: {}, weeklyFlights: [] }
        );
      })
    );
  }

  getTimetableForDays(
    search: Pick<TimetableSearchParams, 'origin' | 'destination' | 'directOnly'>,
    departureDate: LocalDate,
    numberOfDays: number
  ): Observable<TimetableSearchResult> {
    return this.requestTimetable(
      search.origin.code,
      search.destination.code,
      departureDate,
      numberOfDays,
      search.directOnly
    ).pipe(share());
  }

  /**
   * Flights per day for the range, served from a shared per-day cache.
   * Only days not yet loaded or in flight are requested, grouped into contiguous runs of at most 35 days.
   * Days are cached as soon as their request is created, so concurrent callers reuse it rather than refetching.
   * Days whose request failed are left out of the result; errors only if every requested day failed.
   */
  getFlights(
    origin: string,
    destination: string,
    from: LocalDate,
    numberOfDays: number,
    directFlights = false
  ): Observable<Record<string, FinnairBoundItem[]>> {
    const dateIds = numberOfDays > 0 ? getDateIdsInRange(from, from.plusDays(numberOfDays - 1)) : [];
    if (dateIds.length === 0) {
      return of({});
    }

    const routeKey = `${origin}-${destination}-${directFlights}`;
    if (!this.flightsCache.has(routeKey)) {
      this.flightsCache.set(routeKey, new Map());
    }
    const days = this.flightsCache.get(routeKey);
    const missingIds = dateIds.filter((id) => {
      return !days.has(id);
    });
    this.toContiguousRuns(missingIds).forEach((run) => {
      this.registerFlightsRun(days, run, origin, destination, directFlights);
    });

    return combineLatest(
      dateIds.map((id) => {
        return days.get(id);
      })
    ).pipe(
      map((cachedDays) => {
        const loaded = dateIds.flatMap((id, index) => {
          const cachedDay = cachedDays[index];
          return 'flights' in cachedDay ? [[id, cachedDay.flights] as const] : [];
        });
        if (loaded.length === 0) {
          throw (cachedDays[0] as { error: unknown }).error;
        }
        return Object.fromEntries(loaded);
      })
    );
  }

  private toContiguousRuns(dateIds: string[]): string[][] {
    const maxDays = TimetableSearchService.MAX_TIMETABLE_DAYS_PER_REQUEST;
    return dateIds.reduce<string[][]>((runs, id) => {
      const lastRun = runs[runs.length - 1];
      const lastId = lastRun?.[lastRun.length - 1];
      const isContiguous = lastId && new LocalDate(lastId).plusDays(1).id === id;
      if (isContiguous && lastRun.length < maxDays) {
        lastRun.push(id);
      } else {
        runs.push([id]);
      }
      return runs;
    }, []);
  }

  private registerFlightsRun(
    days: Map<string, Observable<CachedDay>>,
    run: string[],
    origin: string,
    destination: string,
    directFlights: boolean
  ): void {
    const entries = new Map<string, Observable<CachedDay>>();
    const request$ = this.requestTimetable(origin, destination, new LocalDate(run[0]), run.length, directFlights).pipe(
      map(({ flights }) => {
        return { flightsByDay: flights ?? {} };
      }),
      // Evict so the next call retries, but only entries still owned by this run, not a newer retry's.
      // The failure is emitted as a value so shareReplay never resets and refetches for a stale subscriber.
      catchError((error: unknown) => {
        entries.forEach((entry, id) => {
          if (days.get(id) === entry) {
            days.delete(id);
          }
        });
        return of({ error });
      }),
      // refCount: false so a consumer's switchMap restart doesn't cancel a request other days depend on.
      shareReplay({ bufferSize: 1, refCount: false })
    );

    run.forEach((id) => {
      const entry = request$.pipe(
        map((result): CachedDay => {
          if ('error' in result) {
            return result;
          }
          // The API omits days without flights; [] marks them as loaded rather than missing.
          return { flights: result.flightsByDay[id] ?? [] };
        })
      );
      entries.set(id, entry);
      days.set(id, entry);
    });
  }

  private requestTimetable(
    origin: string,
    destination: string,
    departureDate: LocalDate,
    numberOfDays: number,
    directFlights: boolean
  ): Observable<TimetableSearchResult> {
    const params = {
      originLocationCode: origin,
      destinationLocationCode: destination,
      departureDate: departureDate.toString(),
      numberOfDays,
      directFlights,
    };

    return this.dapiService.invoke$(getTimetableForRoute, this.configService.cfg.instantSearchUrl, params).pipe(
      timeout(TimetableSearchService.GET_TIMETABLE_TIMEOUT_MS),
      retry({
        count: TimetableSearchService.MAX_RETRIES,
        delay: (err: unknown, retryCount: number) => {
          // Timeouts aren't retried: each attempt already waits 20s and would hold SSR renders open.
          if (
            err instanceof TimeoutError ||
            (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500)
          ) {
            return throwError(() => err);
          }
          return timer(TimetableSearchService.RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1));
        },
      }),
      catchError((err: unknown) => {
        this.monitoringLogger.error('Error fetching timetable flights', {
          error: mapErrorForMonitoring(err),
        });

        return throwError(() => err);
      }),
      map((timetable) => {
        if (!isPresent(timetable)) {
          throw { code: 400, message: 'Malformed response' };
        }

        const bounds: FinnairBoundItem[] = timetable.flights;
        const flights = groupBy(bounds, (bound) =>
          bound.departure.dateTime.substring(0, bound.departure.dateTime.indexOf('T'))
        );

        return {
          availability: timetable.availability,
          flights,
          weeklyFlights: timetable.weeklyFlights,
        };
      })
    );
  }
}
